import ReactPDF from '@react-pdf/renderer';
import kebabCase from 'lodash/kebabCase';
import { CountriesISO3, StringMaxLengths } from '../../../types/enums';
import { Nullable } from '../../../types/common';
import { AdjustedFontSize, MaxPixelsOfString } from '../../../types/badge';
import ReactPDFStyles from '@react-pdf/types';

const getTruncatedString = (value: string, badgeSuffixLength: number = 0) => {
  return value.length > 40 - badgeSuffixLength ? value.slice(0, 40 - badgeSuffixLength) : value;
};

const removeMultipleSpacesFromString = (val: string | undefined) => {
  if (!val) {
    return '';
  }
  return val.replace(/ +(?= )/g, '');
};

const getIdInBrakets = (companyName: string, standId: string) =>
  40 - companyName.length - 3 >= standId.length ? ' (' + standId + ')' : '';

const convertStylesToString = (styles: ReactPDFStyles.Style) => {
  return Object.keys(styles).reduce((acc, key) => {
    return acc + `${kebabCase(key)}: ${styles[key as keyof ReactPDFStyles.Style]};`;
  }, '');
};

const getStringLength = (str: string, initialFontSize: number, styles: ReactPDFStyles.Style) => {
  const span = document.createElement('span');

  span.style.cssText = convertStylesToString(styles);

  span.innerText = str;
  span.style.position = 'absolute';
  span.style.fontSize = `${initialFontSize}px`;
  span.style.opacity = '0';

  const html = document.getElementsByTagName('html')[0];
  html.appendChild(span);
  const maxPixels = Math.max(span.getBoundingClientRect().width);
  span.parentNode?.removeChild(span);
  return maxPixels;
};

const getDisplayCountryNameSeparator = (expression: boolean) => {
  return expression ? ', ' : '';
};

const getDisplayCountryName = (countryCode = '', city = '', stateCode = '') => {
  const withStateSeparator = getDisplayCountryNameSeparator(Boolean(stateCode && city));
  if (countryCode === CountriesISO3.AUS || countryCode === CountriesISO3.USA) {
    return `${city}${withStateSeparator}${stateCode}`;
  }

  if (countryCode === CountriesISO3.TWN) {
    return city;
  }

  const withCountrySeparator = getDisplayCountryNameSeparator(Boolean(countryCode && city));
  return `${city}${withCountrySeparator}${countryCode}`;
};

const getMaxPixelsOfString = ({
  str,
  paperSize,
  initialFontSize,
  additionalMaxWidthLength,
  styles = {}
}: MaxPixelsOfString): number => {
  const maxPixels = getStringLength(str, initialFontSize, styles);
  if (maxPixels <= StringMaxLengths[paperSize] - additionalMaxWidthLength) {
    return initialFontSize;
  }
  return getMaxPixelsOfString({
    str,
    paperSize,
    initialFontSize: initialFontSize - 1,
    additionalMaxWidthLength,
    styles
  });
};

export const getBadgeAdditionalColor = (key: keyof ReactPDF.Styles, color: Nullable<string>) => {
  if (color) {
    return { [key]: `#${color}` } as unknown as ReactPDF.Styles;
  }
  return {};
};

const getMaxLenString = (str1: string, str2: string, fontSize: number, styles = {}) => {
  const str1Len = getStringLength(str1, fontSize, styles);
  const str2Len = getStringLength(str2, fontSize, styles);
  return str1Len > str2Len ? str1 : str2;
};

const getAdjustedFontSize = ({
  str1,
  str2 = str1,
  paperSize,
  initialFontSize,
  additionalMaxWidthLength = 0,
  styles
}: AdjustedFontSize) => {
  const maxLenString = getMaxLenString(str1, str2, initialFontSize, styles);

  return getMaxPixelsOfString({
    str: maxLenString,
    initialFontSize,
    styles,
    paperSize,
    additionalMaxWidthLength
  });
};

export default {
  getTruncatedString,
  removeMultipleSpacesFromString,
  getIdInBrakets,
  getMaxPixelsOfString,
  getAdjustedFontSize,
  getBadgeAdditionalColor,
  getDisplayCountryName
};
